import { UserRole, UserStatus } from '../../enums';

import { z } from 'zod';
import { PhoneNumberPartsValidatorSchema } from '../shared/phoneNumber.schema';

export const UserValidatorSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  mobilePhoneNumber: PhoneNumberPartsValidatorSchema.nullable(),
  // The admin screens send the phone's parts flattened alongside the object.
  countryCallingCode: z.string().optional(),
  localNumber: z.string().optional(),
  confirmedMobilePhoneNumber: z.boolean().optional(),
  confirmedEmail: z.boolean().optional(),
  facebookId: z.string().optional(),
  language: z.string().optional(),
  createdDate: z.string().optional(),
  lastModifiedDate: z.string().optional(),
  status: z.enum(UserStatus),
  roles: z.array(z.enum(UserRole)),
  companyId: z.string().optional(),
});

export type UserValidator = z.infer<typeof UserValidatorSchema>;

export const UserUpdateValidatorSchema = UserValidatorSchema.extend({
  roles: z.array(z.enum(UserRole)).optional(),
  rolesToAdd: z.array(z.enum(UserRole)).optional(),
  rolesToRemove: z.array(z.enum(UserRole)).optional(),
});

export type UserUpdateValidator = z.infer<typeof UserUpdateValidatorSchema>;

export const FanValidatorSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  mobilePhoneNumber: PhoneNumberPartsValidatorSchema.nullable(),
  confirmedMobilePhoneNumber: z.boolean().optional(),
  confirmedEmail: z.boolean().optional(),
  language: z.string(),
  personalInfo: z
    .object({
      address: z
        .object({
          country: z.string().optional().nullable(),
          countryCode: z.string().optional(),
          zipCode: z.string().optional(),
          city: z.string().optional(),
          line1: z.string(),
          line2: z.string().optional().nullable(),
          line3: z.string().optional().nullable(),
          state: z.string().optional().nullable(),
        })
        .optional(),
    })
    .optional(),
});

export type FanValidator = z.infer<typeof FanValidatorSchema>;

export const PasswordValidatorSchema = z.object({
  password: z.string(),
  currentPassword: z.string(),
});

export type PasswordValidator = z.infer<typeof PasswordValidatorSchema>;

export const PhoneValidatorSchema = PhoneNumberPartsValidatorSchema.safeExtend({
  fanGroupId: z.string().optional(),
});

export type PhoneValidator = z.infer<typeof PhoneValidatorSchema>;

export const VerifyCodeValidatorSchema = z.object({
  code: z.string(),
  mobile: PhoneNumberPartsValidatorSchema,
});

export type VerifyCodeValidator = z.infer<typeof VerifyCodeValidatorSchema>;

export const EmailValidatorSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  fanGroupId: z.string().optional(),
  confirmationReturnURLPath: z.string().optional(),
});

export type EmailValidator = z.infer<typeof EmailValidatorSchema>;

export const ResetPasswordValidatorSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  slug: z.string(),
  fanGroupId: z.string().optional(),
});

export type ResetPasswordValidator = z.infer<
  typeof ResetPasswordValidatorSchema
>;

export const UpdatePasswordValidatorSchema = z.object({
  password: z.string(),
  token: z.string(),
});

export type UpdatePasswordValidator = z.infer<
  typeof UpdatePasswordValidatorSchema
>;

export const UserOwnershipValidatorSchema = z.object({
  userId: z.string(),
  fanGroupId: z.string(),
});

export type UserOwnershipValidator = z.infer<
  typeof UserOwnershipValidatorSchema
>;
